import { Link } from "react-router-dom";
import { Download, ArrowRight, ShieldCheck, TrendingUp, FileSearch, Globe2 } from "lucide-react";
import { Reveal } from "@/components/Motion";

const DYNAMICS = [
  { icon: FileSearch, t: "Documentation burden", d: "Professional buyers request batch COAs, microbiological and endotoxin testing, particle characterization, ingredient documentation, stability information and regulatory records before onboarding a new biotechnology-derived cosmetic line." },
  { icon: TrendingUp, t: "Margin compression", d: "Multi-tier distribution can erode the economics needed for education, demonstrations, professional service delivery and customer acquisition." },
  { icon: Globe2, t: "Cross-border execution", d: "Import documentation, product classification, cosmetic claims, bilingual labeling and traceability must be managed consistently to reduce delays and compliance risk." },
];

const PILLARS = [
  { t: "Quality-led onboarding", d: "COA and safety documentation, batch/lot traceability and defined specifications.", v: "Improves professional buyer confidence and due diligence." },
  { t: "Regulatory readiness", d: "Canadian cosmetic notification and import documentation with cosmetic-only claim discipline.", v: "Reduces avoidable classification and claims risk." },
  { t: "Distributor economics", d: "Wholesale model structured around partner growth and viability.", v: "Creates room for clinic/spa resale and treatment-program economics." },
  { t: "Brand architecture", d: "VANTIS™ premium professional positioning.", v: "Supports consistent presentation across markets." },
];

const PORTFOLIO = [
  ["VANTIS — Cosmetic Conditioning Powder (Freeze-Dried)", "1 mL / unit format", "Professional Clinical / Targeted Application"],
  ["VANTIS — Hydrating Facial Mist", "40 mL bottle format", "Daily Support / Homecare Protocol"],
  ["VANTIS — Conditioning Fluid", "10 mL format", "Concentrated Application"],
  ["VANTIS Pro-Mist Atomization", "5 mL × 10 vials format", "Professional Studio / Aesthetic Practice"],
];

const TAKEAWAYS = [
  "Protect the premium position — consistent brand presentation, professional education and documented product specifications.",
  "Make documentation part of the sale — provide qualified partners with the appropriate COA, safety, regulatory and product files.",
  "Preserve partner margin — a professional supply structure supporting clinic/spa resale, education and service delivery.",
  "Control claims and market entry — keep cosmetic claims within permitted boundaries and confirm labeling/import requirements per market.",
  "Scale with traceability — maintain lot-level records and batch-specific documentation as the network expands.",
];

export default function Whitepaper() {
  return (
    <div className="pt-16" data-testid="whitepaper-page">
      <section className="lab-grid border-b border-[#e4e9ec]">
        <div className="mx-auto max-w-[1440px] px-5 pb-16 pt-20 md:px-10 md:pb-20 md:pt-24">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Exoplus Bio Inc. · Strategic industry white paper</p>
            <h1 className="font-display mt-4 max-w-4xl text-[clamp(2.2rem,5vw,4.2rem)] leading-[1.02] tracking-tight" data-testid="whitepaper-headline">
              De-Risking Premium Exosome Cosmetic Distribution <em>& Unlocking High-Margin Professional Programs</em>
            </h1>
            <p className="font-lab mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.16em] text-[#56636b]" data-testid="whitepaper-meta">
              <span>North America focus</span><span>Canada market entry</span><span>Professional channel strategy</span>
            </p>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#56636b]">
              For distributors, professional clinics, medical spas, aesthetic groups and strategic channel partners.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/whitepaper.pdf"
                download
                data-testid="whitepaper-download-button"
                className="inline-flex items-center gap-2.5 bg-[#0a0a0a] px-7 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#0e8c8c]"
              >
                <Download className="h-4 w-4" /> Download the white paper
              </a>
              <Link
                to="/certification"
                data-testid="whitepaper-certification-link"
                className="inline-flex items-center gap-2 border border-[#e4e9ec] bg-white px-7 py-3.5 text-sm font-medium transition-colors hover:border-[#0a0a0a]"
              >
                Certification & compliance <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-5 py-20 md:px-10 md:py-24">
        <Reveal>
          <div className="border-l-2 border-[#0e8c8c] bg-[#e3f2f0]/40 p-7 md:p-9" data-testid="whitepaper-summary">
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Executive summary</p>
            <p className="mt-4 text-[15px] leading-relaxed text-[#0a0a0a]">
              The premium aesthetic and regenerative-cosmetic market is increasingly shaped by three commercial
              requirements: credible product documentation, consistent quality control, and channel economics that
              support margin viability for distributors and professional providers. ExoPlus Bio Inc. has structured its
              VANTIS™ portfolio around these requirements, with a professional-first distribution model designed for
              Canadian and North American market development.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-[#0a0a0a]">
              The portfolio includes freeze-dried cosmetic conditioning powder, hydrating facial mist, conditioning
              fluid and professional-use formats — supported by quality and safety documentation, export-safety
              controls, Canadian import-safety requirements and Canadian Cosmetic Notification Form (CNF) filings as
              applicable. A CNF is a regulatory notification and should not be described as a Health Canada product
              approval.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="font-display mt-20 text-3xl tracking-tight md:text-4xl">01 — Market dynamics & the channel-margin problem</h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {DYNAMICS.map((d, i) => (
            <Reveal key={d.t} delay={i * 0.07}>
              <div className="h-full border border-[#e4e9ec] bg-white p-6" data-testid={`whitepaper-dynamic-${i}`}>
                <d.icon className="h-5 w-5 text-[#0e8c8c]" />
                <h3 className="font-display mt-4 text-lg">{d.t}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-[#56636b]">{d.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <h2 className="font-display mt-20 text-3xl tracking-tight md:text-4xl">02 — The Exoplus commercial framework</h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {PILLARS.map((p, i) => (
            <Reveal key={p.t} delay={(i % 2) * 0.07}>
              <div className="h-full border border-[#e4e9ec] bg-white p-7" data-testid={`whitepaper-pillar-${i}`}>
                <span className="font-lab text-[10px] tracking-[0.2em] text-[#0e8c8c]">0{i + 1}</span>
                <h3 className="font-display mt-3 text-xl">{p.t}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-[#56636b]">{p.d}</p>
                <p className="mt-3 text-[13px] leading-relaxed text-[#0a0a0a]"><strong>Partner value:</strong> {p.v}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <h2 className="font-display mt-20 text-3xl tracking-tight md:text-4xl">03 — Portfolio specifications</h2>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="mt-8 border border-[#e4e9ec]" data-testid="whitepaper-portfolio-table">
            {PORTFOLIO.map(([n, f, c], i) => (
              <div key={n} className={`grid grid-cols-1 gap-1 px-5 py-4 md:grid-cols-12 md:gap-4 ${i < PORTFOLIO.length - 1 ? "border-b border-[#e4e9ec]" : ""}`}>
                <span className="text-sm font-medium md:col-span-6">{n}</span>
                <span className="font-lab text-[12px] text-[#56636b] md:col-span-3">{f}</span>
                <span className="font-lab text-[12px] text-[#0e8c8c] md:col-span-3">{c}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal>
          <h2 className="font-display mt-20 text-3xl tracking-tight md:text-4xl">04 — Quality snapshot</h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#56636b]">
            Representative Lot 08F49 (UC-MSC-derived, 5 mL lyophilized): 59.8 billion particles/mL by Nanoparticle
            Tracking Analysis, mean particle size 76.5 nm, endotoxin &lt; 0.01 EU/mL, viral and mycoplasma screening
            negative. Full testing detail on our certification page.
          </p>
        </Reveal>

        <Reveal>
          <h2 className="font-display mt-20 text-3xl tracking-tight md:text-4xl">05 — Regulatory & cross-border risk controls</h2>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="mt-8 space-y-0 border-t border-[#e4e9ec]">
            {[
              ["Product notification", "Canadian CNF obtained/submitted for applicable products — supporting cosmetic notification compliance; CNF is not product approval."],
              ["Import safety", "Products represented as meeting applicable export-safety controls and Canadian import-safety requirements; documentation accompanies shipments."],
              ["Testing", "Strict quality testing and batch documentation, including representative microbiological and endotoxin results."],
              ["Claims discipline", "Cosmetic-only positioning; no drug, disease-treatment or unapproved therapeutic claims."],
              ["Supplier confidentiality", "Manufacturer identity and specific origin details intentionally omitted to protect the supply chain."],
            ].map(([t, d], i) => (
              <div key={t} className="flex gap-6 border-b border-[#e4e9ec] py-5" data-testid={`whitepaper-control-${i}`}>
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#0e8c8c]" />
                <p className="text-sm leading-relaxed text-[#56636b]"><strong className="text-[#0a0a0a]">{t}.</strong> {d}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal>
          <h2 className="font-display mt-20 text-3xl tracking-tight md:text-4xl">06 — Strategic takeaways</h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TAKEAWAYS.map((t, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06}>
              <div className="h-full border border-[#e4e9ec] bg-white p-6" data-testid={`whitepaper-takeaway-${i}`}>
                <span className="font-lab text-[10px] tracking-[0.2em] text-[#0e8c8c]">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-3 text-[13px] leading-relaxed text-[#56636b]">{t}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-20 border border-[#e4e9ec] bg-white p-7 md:p-10">
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Conclusion</p>
            <p className="mt-4 text-[15px] leading-relaxed text-[#0a0a0a]">
              ExoPlus Bio Inc. is building a professional exosome-cosmetic platform around three practical foundations:
              documented quality, regulatory readiness, and sustainable channel economics — positioned to support
              distributors and professional providers seeking a premium biotechnology-led cosmetic offering with clear
              product architecture and a disciplined approach to cross-border commercialization.
            </p>
            <p className="font-display mt-6 text-xl italic text-[#0e8c8c]">Advanced regenerative beauty. Built for professional channels.</p>
            <p className="mt-8 border-t border-[#e4e9ec] pt-6 text-[12px] leading-relaxed text-[#56636b]" data-testid="whitepaper-notice">
              Important notice: This document is a strategic commercial white paper — not medical advice, and not a
              representation that any cosmetic product is approved by Health Canada. Canadian CNF filing/notification
              is a notification requirement, not pre-market approval. Product claims, regulatory status,
              specifications and batch results should be verified against current product- and lot-specific
              documentation before commercial use.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="/whitepaper.pdf" download data-testid="whitepaper-download-bottom" className="inline-flex items-center gap-2.5 bg-[#0a0a0a] px-7 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#0e8c8c]">
                <Download className="h-4 w-4" /> Download PDF
              </a>
              <Link to="/" data-testid="whitepaper-partner-link" className="inline-flex items-center gap-2 border border-[#e4e9ec] px-7 py-3.5 text-sm font-medium transition-colors hover:border-[#0a0a0a]">
                Become a partner <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}